'use client';

import { useCallback, useState } from 'react';

import type { Document, DocumentViewerState } from '../types';

const MIN_ZOOM = 50;
const MAX_ZOOM = 200;
const DEFAULT_ZOOM = 100;

export function useDocumentViewer(documents: Document[] = []) {
  const [state, setState] = useState<DocumentViewerState>({
    currentDocument: null,
    currentIndex: -1,
    isOpen: false,
    zoom: DEFAULT_ZOOM,
    isFullscreen: false,
  });

  const open = useCallback(
    (document: Document) => {
      const index = documents.findIndex((d) => d.id === document.id);
      setState({
        currentDocument: document,
        currentIndex: index >= 0 ? index : 0,
        isOpen: true,
        zoom: DEFAULT_ZOOM,
        isFullscreen: false,
      });
    },
    [documents],
  );

  const close = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: false,
      currentDocument: null,
      zoom: DEFAULT_ZOOM,
      isFullscreen: false,
    }));
  }, []);

  const navigate = useCallback(
    (direction: 'prev' | 'next') => {
      setState((prev) => {
        const newIndex =
          direction === 'prev'
            ? Math.max(0, prev.currentIndex - 1)
            : Math.min(documents.length - 1, prev.currentIndex + 1);

        if (newIndex === prev.currentIndex) return prev;

        return {
          ...prev,
          currentIndex: newIndex,
          currentDocument: documents[newIndex] || null,
          zoom: DEFAULT_ZOOM,
        };
      });
    },
    [documents],
  );

  const setZoom = useCallback((zoom: number) => {
    setState((prev) => ({
      ...prev,
      zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom)),
    }));
  }, []);

  const zoomIn = useCallback(
    () => setZoom(state.zoom + 25),
    [state.zoom, setZoom],
  );
  const zoomOut = useCallback(
    () => setZoom(state.zoom - 25),
    [state.zoom, setZoom],
  );
  const resetZoom = useCallback(() => setZoom(DEFAULT_ZOOM), [setZoom]);

  const toggleFullscreen = useCallback(() => {
    setState((prev) => ({ ...prev, isFullscreen: !prev.isFullscreen }));
  }, []);

  const hasPrevious = state.currentIndex > 0;
  const hasNext = state.currentIndex < documents.length - 1;

  return {
    ...state,
    hasPrevious,
    hasNext,
    open,
    close,
    navigate,
    setZoom,
    zoomIn,
    zoomOut,
    resetZoom,
    toggleFullscreen,
  };
}

export type UseDocumentViewerReturn = ReturnType<typeof useDocumentViewer>;
